// src/components/ConfirmationModal/ConfirmationModal.js
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ConfirmationModal.css';
import { 
  FiAlertTriangle, 
  FiLoader, 
  FiSettings, 
  FiExternalLink,
  FiCheckCircle,
  FiArrowRight,
  FiClock,
  FiZap
} from 'react-icons/fi';

// Constants
const MIN_GAS_PRICE = "0.000001"; // Minimum gas price

/**
 * Format gas price for display, ensuring very small values are shown appropriately
 * @param {string|number} gweiValue - Gas price in gwei
 * @returns {string} - Formatted gas price for display
 */
const formatGasPriceForDisplay = (gweiValue) => {
  if (!gweiValue || isNaN(parseFloat(gweiValue))) return '0.00';
  
  const value = parseFloat(gweiValue);
  if (value === 0) return '0.00';
  
  // Convert to string to find leading zeros after decimal
  const strValue = value.toString();
  const decimalIndex = strValue.indexOf('.');
  
  // If no decimal or value >= 0.1, show 3 decimal places
  if (decimalIndex === -1 || value >= 0.1) {
    return value.toFixed(8);
  }
  
  // For values with leading zeros after decimal
  let leadingZeros = 0;
  let index = decimalIndex + 1;
  
  // Count leading zeros after decimal point
  while (index < strValue.length && strValue[index] === '0') {
    leadingZeros++;
    index++;
  }
  
  // Format with 5 significant digits after the first non-zero digit
  const totalDecimals = leadingZeros + 5;
  return value.toFixed(totalDecimals);
};

/**
 * Ensure gas price is valid and above minimum
 * @param {string|number} gasPriceGwei - Gas price in gwei
 * @returns {string} - Valid gas price
 */
const ensureSafeGasPrice = (gasPriceGwei) => {
  if (!gasPriceGwei || gasPriceGwei === '' || parseFloat(gasPriceGwei) <= 0) {
    return MIN_GAS_PRICE;
  }
  
  // Ensure price is a valid string for further processing
  const priceStr = gasPriceGwei.toString();
  
  // If gas price is too low, use the minimum
  if (parseFloat(priceStr) < parseFloat(MIN_GAS_PRICE)) {
    return MIN_GAS_PRICE;
  }
  
  return priceStr;
};

/**
 * Calculate gas fee in native currency based on gas price and gas limit
 * @param {string} gasPriceGwei - Gas price in gwei
 * @param {number} gasLimit - Gas limit for the transaction
 * @returns {string} - Gas fee in native currency
 */
const calculateGasFee = (gasPriceGwei, gasLimit = 21000) => {
  if (!gasPriceGwei || isNaN(parseFloat(gasPriceGwei))) return "0";
  
  // Calculate fee: gas price (gwei) * gas limit / 10^9 = native currency amount
  const gweiValue = parseFloat(gasPriceGwei);
  const feeInNative = (gweiValue * gasLimit) / 1000000000;
  
  // Format fee values with appropriate precision
  if (feeInNative === 0) return "0";
  
  // Convert to string to find leading zeros after decimal
  const strValue = feeInNative.toString();
  const decimalIndex = strValue.indexOf('.');
  
  // If no decimal or value >= 0.1, show fixed decimal places
  if (decimalIndex === -1 || feeInNative >= 0.001) {
    return feeInNative.toFixed(10);
  }
  
  // For values with leading zeros after decimal
  let leadingZeros = 0;
  let index = decimalIndex + 1;
  
  // Count leading zeros after decimal point
  while (index < strValue.length && strValue[index] === '0') {
    leadingZeros++;
    index++;
  }
  
  // Format with 5 significant digits after the first non-zero digit
  const totalDecimals = leadingZeros + 8;
  return feeInNative.toFixed(totalDecimals);
};

/**
 * Helper function to shorten an Ethereum address
 * @param {string} address - Ethereum address to shorten
 * @returns {string} Shortened address
 */
function shortenAddress(address) {
  if (!address) return '';
  return address.slice(0, 6) + '...' + address.slice(-4);
}

/**
 * TransactionDetails component displays transaction information
 */
const TransactionDetails = ({ 
  details,
  txnHash, 
  showGasSettings, 
  toggleGasSettings,
  gasOption,
  gasPrice,
  calculatedGasFee,
  currentChain
}) => {
  const { t } = useTranslation();
  
  // Get currency symbol - use what's in the transaction details or fall back to chain symbol
  const currencySymbol = useMemo(() => {
    // Try to extract from the transaction gas fee if available
    if (details?.gasFee && details.gasFee.includes(' ')) {
      const parts = details.gasFee.split(' ');
      if (parts.length > 1) return parts[1].split('(')[0].trim();
    }
    
    // Otherwise use the chain's native currency
    return currentChain?.nativeCurrency?.symbol || 'ETH';
  }, [details?.gasFee, currentChain]);

  // Use the dynamically calculated gas fee if available
  const gasFeeDisplay = useMemo(() => {
    if (calculatedGasFee) {
      return `${calculatedGasFee} ${currencySymbol} (${gasOption})`;
    }
    return details?.gasFee || "Calculating...";
  }, [calculatedGasFee, gasOption, currencySymbol, details?.gasFee]);
  
  // Parse and format the gas fee for better display
  const parseGasFee = (gasFee) => {
    if (!gasFee) return { amount: "...", token: "", speed: "", usdValue: "" };
    
    try {
      // Extract main parts
      const mainPart = gasFee.split('(')[0].trim();
      const parts = mainPart.split(' ');
      
      // Amount and token
      const amount = parts[0];
      const token = parts.length > 1 ? parts[1] : "";
      
      // USD value if available
      let usdValue = "";
      if (gasFee.includes('(') && gasFee.includes('$')) {
        usdValue = gasFee.substring(
          gasFee.indexOf('(') + 1, 
          gasFee.indexOf('$') + 1
        );
      }
      
      // Speed info if available
      let speed = "";
      if (gasFee.includes('(')) {
        speed = gasFee.substring(gasFee.indexOf('('));
      }
      
      return { amount, token, speed, usdValue };
    } catch (error) {
      console.error("Error parsing gas fee:", error);
      return { amount: gasFee, token: "", speed: "", usdValue: "" };
    }
  };

  // Parse the gas fee
  const gasFeeInfo = parseGasFee(gasFeeDisplay);
  
  // Extract currency symbol and amount from spendingAmount if available
  const [spendingAmount, currencySymbolFromAmount] = details?.spendingAmount 
    ? details.spendingAmount.split(' ')
    : ["0", ""];

  return (
    <div className="transaction-details">
      <div className="transaction-summary">
        {details?.type && (
          <div className="transaction-type">
            {details.type}
          </div>
        )}
        
        {details?.spendingAmount && (
          <div className="transaction-amount">
            <span className="amount-value">{spendingAmount}</span>
            <span className="amount-currency">{currencySymbolFromAmount}</span>
          </div>
        )}
      </div>
      
      {details?.from && details?.to && (
        <div className="address-flow-container">
          <div className="address-flow">
            <div className="address-box from-address">
              <div className="address-label">{t('confirmationModal.transaction.from')}</div>
              <div className="address-value">{shortenAddress(details.from)}</div>
            </div>
            <div className="arrow-icon">
              <FiArrowRight />
            </div>
            <div className="address-box to-address">
              <div className="address-label">{t('confirmationModal.transaction.to')}</div>
              <div className="address-value">{shortenAddress(details.to)}</div>
            </div>
          </div>
        </div>
      )}
      
      <div className="gas-fee-section">
        <div className="gas-fee-header">
          <div className="gas-label-container">
            <span className="gas-emoji">⛽</span>
            <span className="detail-label">{t('confirmationModal.transaction.gasFee')}</span>
          </div>
          {!txnHash && (
            <button 
              className="gas-settings-btn" 
              onClick={toggleGasSettings}
              title="Change gas settings"
            >
              <FiSettings />
              <span>{t('confirmationModal.transaction.adjust')}</span>
            </button>
          )}
        </div>
        
        <div className="gas-fee-display">
          <div className="gas-fee-primary">
            <span className="gas-fee-amount">{gasFeeInfo.amount}</span>
            <span className="gas-fee-token">{gasFeeInfo.token}</span>
          </div>
          
          {gasFeeInfo.usdValue && (
            <div className="gas-fee-secondary">
              <span className="gas-fee-usd">${gasFeeInfo.usdValue}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * GasSettings component for adjusting transaction gas settings
 */
const GasSettings = ({ 
  gasOption, 
  setGasOption, 
  gasPrice, 
  setGasPrice, 
  dynamicGasPrice, 
  onGasSettingChange 
}) => {
  const { t } = useTranslation();
  
  // Log initial props for debugging
  useEffect(() => {
    console.log('GasSettings initialized with:', { 
      gasOption, 
      gasPrice, 
      dynamicGasPrice 
    });
  }, []);

  // Calculate gas price for a given option, ensuring it's never below minimum
  const getGasPriceForOption = (option) => {
    if (!dynamicGasPrice) return ensureSafeGasPrice(gasPrice);
    
    const basePrice = parseFloat(dynamicGasPrice);
    let calculatedPrice;
    
    switch (option) {
      case 'slow': 
        calculatedPrice = (basePrice * 0.8).toString();
        break;
      case 'medium': 
        calculatedPrice = basePrice.toString();
        break;
      case 'fast': 
        calculatedPrice = (basePrice * 1.2).toString();
        break;
      case 'custom': 
        return gasPrice;
      default: 
        calculatedPrice = basePrice.toString();
    }
    
    return ensureSafeGasPrice(calculatedPrice);
  };
  
  const getEtaForOption = (option) => {
    switch (option) {
      case 'slow': return t('confirmationModal.gasSettings.eta.slow');
      case 'medium': return t('confirmationModal.gasSettings.eta.medium');
      case 'fast': return t('confirmationModal.gasSettings.eta.fast');
      default: return t('confirmationModal.gasSettings.eta.medium');
    }
  };
  
  // Handle gas option change - calculate and set new gas price
  const handleGasOptionChange = (option) => {
    console.log(`Gas option change requested: ${option}`);
    
    // Set the gas option
    setGasOption(option);
    
    // For non-custom options, calculate the appropriate gas price
    if (option !== 'custom') {
      const calculatedPrice = getGasPriceForOption(option);
      setGasPrice(calculatedPrice);
      
      // IMPORTANT: Call onGasSettingChange to update parent component
      if (onGasSettingChange) {
        console.log(`Setting option=${option}, price=${calculatedPrice}`);
        onGasSettingChange(option, calculatedPrice);
      }
    }
  };
  
  // Handle custom gas price changes
  const handleCustomGasPriceChange = (e) => {
    let value = e.target.value;
    
    // Ensure the value is not below minimum
    if (value !== '' && parseFloat(value) < parseFloat(MIN_GAS_PRICE)) {
      value = MIN_GAS_PRICE;
    }
    
    setGasPrice(value);
    
    if (gasOption !== 'custom') {
      setGasOption('custom');
    }
    
    // IMPORTANT: Call onGasSettingChange to update parent component
    if (onGasSettingChange) {
      console.log(`Setting custom gas price: option=custom, price=${value}`);
      onGasSettingChange('custom', value);
    }
  };

  return (
    <div className="gas-settings-panel">
      <h3><FiZap className="settings-icon" /> {t('confirmationModal.gasSettings.title')}</h3>
      <div className="gas-options">
        <div 
          className={`gas-option ${gasOption === 'slow' ? 'selected' : ''}`}
          onClick={() => handleGasOptionChange('slow')}
        >
          <div className="option-label">
            <FiClock className="option-icon slow" />
            <span>{t('confirmationModal.gasSettings.slow')}</span>
          </div>
          <div className="option-details">
            <div className="option-price">{formatGasPriceForDisplay(getGasPriceForOption('slow'))} {t('confirmationModal.gasSettings.gwei')}</div>
            <div className="option-time">{getEtaForOption('slow')}</div>
          </div>
        </div>
        <div 
          className={`gas-option ${gasOption === 'medium' ? 'selected' : ''}`}
          onClick={() => handleGasOptionChange('medium')}
        >
          <div className="option-label">
            <FiClock className="option-icon medium" />
            <span>{t('confirmationModal.gasSettings.medium')}</span>
          </div>
          <div className="option-details">
            <div className="option-price">{formatGasPriceForDisplay(getGasPriceForOption('medium'))} {t('confirmationModal.gasSettings.gwei')}</div>
            <div className="option-time">{getEtaForOption('medium')}</div>
          </div>
        </div>
        <div 
          className={`gas-option ${gasOption === 'fast' ? 'selected' : ''}`}
          onClick={() => handleGasOptionChange('fast')}
        >
          <div className="option-label">
            <FiZap className="option-icon fast" />
            <span>{t('confirmationModal.gasSettings.fast')}</span>
          </div>
          <div className="option-details">
            <div className="option-price">{formatGasPriceForDisplay(getGasPriceForOption('fast'))} {t('confirmationModal.gasSettings.gwei')}</div>
            <div className="option-time">{getEtaForOption('fast')}</div>
          </div>
        </div>
        
        {/* Modified custom gas option */}
        <div 
          className={`gas-option custom-option ${gasOption === 'custom' ? 'selected' : ''}`}
          onClick={() => handleGasOptionChange('custom')}
        >
          <div className="option-label">
            <input
              type="checkbox"
              checked={gasOption === 'custom'}
              onChange={() => handleGasOptionChange('custom')}
              onClick={(e) => e.stopPropagation()}
            />
            <span>{t('confirmationModal.gasSettings.customGasPrice')}</span>
          </div>
          <div className="custom-input-container" onClick={(e) => e.stopPropagation()}>
            <input
              type="number"
              min={MIN_GAS_PRICE}
              step="0.000001"
              className="custom-gas-input"
              value={gasPrice}
              onChange={handleCustomGasPriceChange}
              disabled={gasOption !== 'custom'}
            />
            <span className="gwei-label">{t('confirmationModal.gasSettings.gwei')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * TransactionSuccess component shows transaction confirmation and hash
 */
const TransactionSuccess = ({ txnHash, explorerUrl }) => {
  const { t } = useTranslation();
  
  return (
    <div className="transaction-success">
      <div className="success-icon-container">
        <FiCheckCircle className="success-large-icon" />
      </div>
      <div className="success-content">
        <p className="success-message">{t('confirmationModal.success.title')}</p>
        <div className="success-subtitle">{t('confirmationModal.success.subtitle')}</div>
      </div>
      <div className="hash-container">
        <div className="hash-header">
          <span className="hash-label">{t('confirmationModal.success.hash')}</span>
          <a 
            href={`${explorerUrl}/tx/${txnHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="view-link"
          >
            <FiExternalLink className="link-icon" />
            <span>{t('confirmationModal.success.view')}</span>
          </a>
        </div>
        <div className="hash-value">{txnHash.slice(0, 16) + '...' + txnHash.slice(-8)}</div>
      </div>
    </div>
  );
};

/**
 * ModalFooter component with action buttons
 */
const ModalFooter = ({ txnHash, isConfirming, onConfirm, onClose, explorerUrl }) => {
  const { t } = useTranslation();
  
  return (
    <div className="modal-footer">
      {!txnHash ? (
        <>
          <button className="confirm-btn" onClick={onConfirm} disabled={isConfirming}>
            {isConfirming ? (
              <>
                <FiLoader className="button-icon spin" /> {t('confirmationModal.buttons.processing')}
              </>
            ) : (
              t('confirmationModal.buttons.confirm')
            )}
          </button>
          {/* Only show Cancel button when not confirming */}
          {!isConfirming && (
            <button className="cancel-btn" onClick={onClose}>{t('confirmationModal.buttons.cancel')}</button>
          )}
        </>
      ) : (
        <button className="close-btn" onClick={onClose}>{t('confirmationModal.buttons.close')}</button>
      )}
    </div>
  );
};

/**
 * ConfirmationModal component
 * Displays transaction details and handles confirmation
 */
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  transactionDetails, 
  txnHash,
  explorerUrl,
  currentGasOption,
  currentGasPrice,
  dynamicGasPrice,
  onGasSettingChange,
  estimatedGasLimit = 21000, // Default to standard ETH transfer gas limit
  currentChain // Pass the current chain object
}) => {
  const { t } = useTranslation();
  
  // Component state
  const [isConfirming, setIsConfirming] = useState(false);
  const [showGasSettings, setShowGasSettings] = useState(false);
  const [localGasOption, setLocalGasOption] = useState(currentGasOption || 'medium');
  const [localGasPrice, setLocalGasPrice] = useState(
    ensureSafeGasPrice(currentGasPrice || '3')
  );
  
  // Calculate gas fee based on current gas price and estimated gas limit
  const calculatedGasFee = useMemo(() => {
    return calculateGasFee(localGasPrice, estimatedGasLimit);
  }, [localGasPrice, estimatedGasLimit]);
  
  // Log key information for debugging
  useEffect(() => {
    console.log('ConfirmationModal mounted with settings:', { 
      currentGasOption, 
      currentGasPrice, 
      dynamicGasPrice,
      estimatedGasLimit,
      currentChain: currentChain?.name
    });
    
    // Set initial local state
    setLocalGasOption(currentGasOption || 'medium');
    setLocalGasPrice(ensureSafeGasPrice(currentGasPrice || '3'));
  }, []);
  
  // Update local state when props change
  useEffect(() => {
    if (currentGasOption && currentGasOption !== localGasOption) {
      console.log(`Updating localGasOption from ${localGasOption} to ${currentGasOption}`);
      setLocalGasOption(currentGasOption);
    }
    
    if (currentGasPrice) {
      const safeGasPrice = ensureSafeGasPrice(currentGasPrice);
      if (safeGasPrice !== localGasPrice) {
        console.log(`Updating localGasPrice from ${localGasPrice} to ${safeGasPrice}`);
        setLocalGasPrice(safeGasPrice);
      }
    }
  }, [currentGasOption, currentGasPrice]);
  
  // Update gas settings when dynamic gas price changes
  useEffect(() => {
    if (!dynamicGasPrice) return;
    
    // Only update if not using custom gas price
    if (localGasOption !== 'custom') {
      let calculatedPrice;
      
      switch (localGasOption) {
        case 'slow':
          calculatedPrice = (parseFloat(dynamicGasPrice) * 0.8).toString();
          break;
        case 'medium':
          calculatedPrice = dynamicGasPrice;
          break;
        case 'fast':
          calculatedPrice = (parseFloat(dynamicGasPrice) * 1.2).toString();
          break;
        default:
          calculatedPrice = dynamicGasPrice;
      }
      
      const safeGasPrice = ensureSafeGasPrice(calculatedPrice);
      
      // Only update if the price has actually changed
      if (safeGasPrice !== localGasPrice) {
        console.log(`Updating gas price based on network: ${safeGasPrice}`);
        setLocalGasPrice(safeGasPrice);
        
        // Notify the parent component of the updated settings
        if (onGasSettingChange) {
          console.log(`Notifying parent of gas settings change: option=${localGasOption}, price=${safeGasPrice}`);
          onGasSettingChange(localGasOption, safeGasPrice);
        }
      }
    }
  }, [dynamicGasPrice]);

  // Toggle gas settings panel visibility
  const toggleGasSettings = () => {
    console.log(`Toggling gas settings panel: ${!showGasSettings}`);
    setShowGasSettings(!showGasSettings);
  };

  // Handler for gas setting changes in child component
  const handleGasSettingChange = (option, price) => {
    console.log(`handleGasSettingChange called with option=${option}, price=${price}`);
    
    // First ensure the price is safe
    const safePrice = ensureSafeGasPrice(price);
    
    // Update local state
    setLocalGasOption(option);
    setLocalGasPrice(safePrice);
    
    // Pass changes up to parent - THIS IS CRITICAL
    if (onGasSettingChange) {
      console.log(`Sending gas settings to parent: option=${option}, price=${safePrice}`);
      onGasSettingChange(option, safePrice);
    }
  };

  // Handle confirmation button click
  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      // Ensure we're using valid gas settings
      const safeGasPrice = ensureSafeGasPrice(localGasPrice);
      
      console.log('Confirming transaction with gas settings:', {
        gasOption: localGasOption,
        gasPrice: safeGasPrice
      });
      
      // Pass the current gas settings to onConfirm
      // This is crucial - make sure we pass BOTH gasOption AND gasPrice
      await onConfirm({
        gasOption: localGasOption,
        gasPrice: safeGasPrice
      });
    } catch (error) {
      console.error("Transaction confirmation error:", error);
      // Close the modal if there's an error
      onClose();
    } finally {
      setIsConfirming(false);
    }
  };

  // Prevent clicking the overlay from closing the modal during confirmation
  const handleOverlayClick = (e) => {
    if (e.target.className === 'confirmation-modal-overlay' && !isConfirming) {
      onClose();
    }
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <div className="confirmation-modal-overlay" onClick={handleOverlayClick}>
      <div className={`confirmation-modal ${txnHash ? 'success-mode' : ''}`}>
        {/* Close button in top right - always visible */}
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          ✕
        </button>
        
        {/* Modal header */}
        <div className="modal-header">
          {!txnHash ? (
            <>
              <FiAlertTriangle className="modal-icon warning" />
              <h2>{t('confirmationModal.header.confirm')}</h2>
            </>
          ) : (
            <h2>{t('confirmationModal.header.sent')}</h2>
          )}
        </div>
        
        {/* Modal body */}
        <div className="modal-body">
          {/* Transaction details section */}
          {!txnHash && (
            <TransactionDetails 
              details={transactionDetails} 
              txnHash={txnHash}
              showGasSettings={showGasSettings}
              toggleGasSettings={toggleGasSettings}
              gasOption={localGasOption}
              gasPrice={localGasPrice}
              calculatedGasFee={calculatedGasFee}
              currentChain={currentChain}
            />
          )}
          
          {/* Gas Settings Panel */}
          {showGasSettings && !txnHash && (
            <GasSettings 
              gasOption={localGasOption}
              setGasOption={setLocalGasOption}
              gasPrice={localGasPrice}
              setGasPrice={setLocalGasPrice}
              dynamicGasPrice={dynamicGasPrice}
              onGasSettingChange={handleGasSettingChange}
            />
          )}
          
          {/* Transaction success or confirmation message */}
          {txnHash ? (
            <TransactionSuccess txnHash={txnHash} explorerUrl={explorerUrl} />
          ) : (
            <p className="confirmation-text">
              {t('confirmationModal.transaction.review')}
            </p>
          )}
        </div>
        
        {/* Modal footer with action buttons */}
        <ModalFooter 
          txnHash={txnHash}
          isConfirming={isConfirming}
          onConfirm={handleConfirm}
          onClose={onClose}
          explorerUrl={explorerUrl}
        />
      </div>
    </div>
  );
};

export default ConfirmationModal;